import { useContext, useEffect, useState } from "react";
import { mapUtil } from "@stormwater-software/common";
import uploadApi from "../../utils/api/upload";
import AppContext from "../../contexts/AppContext";
import { getOfflineSiteMapUploads } from "../../db/db";
import { withOfflineFallback } from "../../utils/offlineFallback";

interface ISiteMap {
  id: number;
}

export function useSiteMaps(
  projectId: number,
  projectDocTypeId: number | null,
  onError: (error: string) => void,
): ISiteMap[] | null {
  const [state, setState] = useState<ISiteMap[] | null>(null);
  const appContext = useContext(AppContext);

  useEffect(() => {
    // This effect runs twice — once before projectDocTypeId has arrived and once
    // after — and the two branches race. Without this flag the first (cache) read
    // could resolve after the second (network) one and overwrite fresh data with
    // cached data, which is a live risk at startup while IndexedDB is busy.
    let cancelled = false;

    const supported = (maps: any[]) =>
      maps.filter((m: any) => mapUtil.isSupportedMimeType(m.upload.mime_type));

    // projectDocTypeId arrives asynchronously, so it is null on first render.
    // With it: query the API, falling back to the download cache when the
    // request fails (navigator.onLine cannot be trusted). Without it: the cache
    // is the only option, and finding nothing there is NOT an error — the id may
    // still be on its way, and this effect re-runs when it lands.
    if (projectDocTypeId) {
      appContext.loadData(
        () =>
          withOfflineFallback(
            () => uploadApi.project.index(projectId, projectDocTypeId),
            // Filtered to the requested doc type so the offline list matches the
            // online one on projects with sub-doc-types.
            () => getOfflineSiteMapUploads(projectId, projectDocTypeId),
          )
            .then(maps => {
              if (!cancelled) {
                setState(maps ? supported(maps) : []);
              }
            })
            .catch(err => {
              if (cancelled) {
                return;
              }
              console.error("useProjectSiteMaps", err);
              onError("Cannot get project site maps");
            }),
        "Site Maps",
      );

      return () => {
        cancelled = true;
      };
    }

    // Skipped when the browser is confident it is online: this branch cannot
    // filter by doc type yet, so rendering its result would flash a stale or
    // wrong-doc-type list at online users before the real fetch resolves.
    if (navigator.onLine) {
      return () => {
        cancelled = true;
      };
    }

    getOfflineSiteMapUploads(projectId)
      .then(maps => {
        if (maps && !cancelled) {
          setState(supported(maps));
        }
      })
      .catch(err => console.error("useProjectSiteMaps (cache)", err));

    return () => {
      cancelled = true;
    };
  }, [projectDocTypeId, projectId]);

  return state;
}
